import { Component } from 'preact';
import { connect } from 'unistore/preact';
import { Text } from 'preact-i18n';
import cx from 'classnames';
import {
  WEBSOCKET_MESSAGE_TYPES,
  DEVICE_FEATURE_UNITS,
  THERMOSTAT_MODE,
  THERMOSTAT_PRESET,
  PILOT_WIRE_MODE
} from '../../../../../server/utils/constants';
import { celsiusToFahrenheit, fahrenheitToCelsius } from '../../../../../server/utils/units';
import { calendarDaysAhead } from '../../../../../server/utils/thermostatSchedule';
import {
  defaultPresetTemps,
  DEFAULT_MIN_TEMP,
  DEFAULT_MAX_TEMP,
  DEFAULT_HYSTERESIS_START,
  DEFAULT_WINDOW_DELAY_SECONDS,
  MAX_WINDOW_DELAY_SECONDS,
  DEFAULT_PILOT_WIRE_ORDERS,
  supportedPilotWireOrder
} from '../../../../../server/utils/thermostatConstants';
import withIntlAsProp from '../../../utils/withIntlAsProp';
import style from './style.module.css';
import {
  getPresetColor as presetColorForMode,
  readableTextOn,
  counterDarkModeInvert
} from '../../../utils/thermostatPresetColors';
import CircularGauge from './CircularGauge';
import { angleToTemp as angleToSetpoint, getAngleFromPointer, isAngleInArc } from './gaugeGeometry';
import { loadDeviceConfig } from './deviceConfig';
import { isRunningFromStateFeature } from './operatingState';

const PRESET_ICONS = {
  off: 'fe-power',
  frost: 'fe-snowflake',
  away: 'fe-user-x',
  eco: 'fe-feather',
  night: 'fe-moon'
};
// Comfort is the only preset whose icon depends on the mode: it means "the
// temperature you want when you are here", which is a flame when heating and a
// snowflake when cooling. A sun for both read as "warm" even in cooling.
const COMFORT_ICON = { heating: 'fe-flame', cooling: 'fe-snowflake' };
const HEATING_PRESETS = ['off', 'frost', 'away', 'eco', 'night', 'comfort'];
const COOLING_PRESETS = ['off', 'comfort'];
// A preset the thermostat can be started back on: one that names a temperature.
const isStartPreset = preset => preset !== 'off' && [...HEATING_PRESETS, ...COOLING_PRESETS].includes(preset);
// The preset feature carries an integer; the widget speaks in names. `off` is in
// neither table: stopping is a mode, and it goes to the mode feature.
const PRESET_VALUES = {
  schedule: THERMOSTAT_PRESET.SCHEDULE,
  frost: THERMOSTAT_PRESET.FROST,
  away: THERMOSTAT_PRESET.AWAY,
  eco: THERMOSTAT_PRESET.ECO,
  night: THERMOSTAT_PRESET.NIGHT,
  comfort: THERMOSTAT_PRESET.COMFORT
};
const PRESET_NAMES = Object.keys(PRESET_VALUES).reduce((acc, name) => ({ ...acc, [PRESET_VALUES[name]]: name }), {});
// The pilot-wire orders, keyed as the device page already names them.
const PILOT_WIRE_ORDER_KEYS = {
  [PILOT_WIRE_MODE.OFF]: 'off',
  [PILOT_WIRE_MODE.FROST_PROTECTION]: 'frost-protection',
  [PILOT_WIRE_MODE.ECO]: 'eco',
  [PILOT_WIRE_MODE.COMFORT_2]: 'comfort_-2',
  [PILOT_WIRE_MODE.COMFORT_1]: 'comfort_-1',
  [PILOT_WIRE_MODE.COMFORT]: 'comfort'
};
// The preset whose colour and icon an order takes on the card. The derived
// comforts are comfort chosen on the heater, and look like it.
const PRESET_OF_PILOT_WIRE_ORDER = {
  [PILOT_WIRE_MODE.OFF]: 'off',
  [PILOT_WIRE_MODE.FROST_PROTECTION]: 'frost',
  [PILOT_WIRE_MODE.ECO]: 'eco',
  [PILOT_WIRE_MODE.COMFORT_2]: 'comfort',
  [PILOT_WIRE_MODE.COMFORT_1]: 'comfort',
  [PILOT_WIRE_MODE.COMFORT]: 'comfort'
};
// Sentinel: hold every incoming setpoint, whatever its value
const HOLD_ANY_SETPOINT = Symbol('hold-any-setpoint');

// The time a banner hands back at, with its day when it is not today: "until
// 13:00" read as today for a hold ending at 13:00 tomorrow (forum 11074). A
// week ahead falls on today's weekday, which "next" tells apart from today.
// `weekday` is 0 for Monday, as in a schedule.
const untilText = (days, weekday, time, t) => {
  if (days === 0) return time;
  if (days === 1) return `${(t && t.untilTomorrow) || ''} ${time}`;
  const names = days === 7 ? t && t.untilNextDays : t && t.untilDays;
  return `${(names && names[weekday]) || ''} ${time}`;
};

// Parse a value as a finite number with a fallback — unlike `Number(x) || d`, a legitimate 0 is kept
const numOr = (value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

class ThermostatBox extends Component {
  state = {
    setpoint: null,
    currentTemp: null,
    humidity: null,
    activePreset: null,
    isManualMode: false,
    error: false,
    noConfig: false,
    remoteConfig: null,
    featureMin: null,
    featureMax: null,
    featureUnit: null,
    activeSchedule: null,
    manualUntil: null,
    // The phone's day the hold banner was last drawn on (see refreshClock)
    today: new Date().toDateString(),
    isWindowOpen: false,
    // Real state of the actuator, as reported by the switch feature. The widget
    // reflects it rather than recomputing it: hysteresis has a neutral zone where
    // the server holds the current state, and that memory cannot be re-derived
    // from the temperature alone.
    isSwitchOn: null,
    // The order a pilot-wire heater carries, shown when it regulates alone: it is
    // all the card knows of what the heater does.
    pilotWireOrder: null,
    // The orders its module declares, when it does (supportedPilotWireOrder).
    pilotWireSupportedOptions: null,
    // A mode this card has written and no reload of the device has carried yet
    // (see writeMode). Read before the device's own mode, so a tap on Off or on
    // a way out of it shows at once, and a reload already in flight when the
    // user tapped cannot bring the old mode back.
    pendingMode: null,
    // Whether the first load has settled the setpoint. Until then the gauge
    // jumps rather than glides: on a °F feature under a °C thermostat it is
    // first drawn on the unconverted value, then corrected once the feature's
    // unit is read, and that correction is no change of setpoint.
    setpointLoaded: false
  };

  svgRef = null;
  timezone = null;
  // Per sensor, since a thermostat may average several probes (spec A.1.1), each
  // with its own unit: the unit from the initial GET, the last reading in the
  // thermostat's unit, and whether each opening sensor reads open.
  sensorUnits = {};
  temperatureReadings = {};
  openWindows = {};
  thermostatUnit = null;
  savingPreset = false;
  // True while selectPreset writes: the hold it may arm is the widget's own, so
  // the preset it just lit must not be un-highlighted by the server's echo.
  pickingPreset = false;
  lastActivePreset = null;
  lastPresetWrite = null;
  expectedSetpoint = null;
  expectedSetpointTimer = null;
  // The mode values this card has just written, each with the timer that forgets it.
  expectedModes = new Map();

  getConfig = () => ({ ...this.props.box, ...(this.state.remoteConfig || {}) });

  // A real thermostat driven by its setpoint. A pilot-wire heater never is (spec
  // C.1.1): it owns its setpoint feature like a virtual thermostat. `external`
  // with a pilot wire is how one regulating itself was saved before the
  // `pilot-wire` type existed.
  isExternal = () => {
    const cfg = this.getConfig();
    return cfg.thermostat_type === 'external' && !cfg.pilot_wire_feature;
  };

  // Which feature tells the widget the equipment is running: the switch Gladys
  // drives on a virtual thermostat, the real device's state feature on an
  // external one. An external thermostat that exposes neither returns null, and
  // the widget estimates the state from the setpoint instead.
  //
  // A pilot-wire heater has no contact to read, and the order it carries says
  // nothing of whether it draws current. When Gladys regulates it, the server
  // decides on the room sensor and says so on the thermostat's own
  // operating-state feature. When the heater regulates, nothing tells: the card
  // shows the order sent instead (see isHeaterRegulated).
  getRunningStateFeature = () => {
    const cfg = this.state.remoteConfig;
    if (!cfg) return null;
    if (this.isExternal()) return cfg.state_feature || null;
    if (cfg.pilot_wire_feature) return this.isHeaterRegulated() ? null : cfg.operatingStateFeature || null;
    return cfg.switch_feature || null;
  };

  // A pilot-wire heater left to its own thermostat (spec C.1.1): a `pilot-wire`
  // thermostat, or a virtual one with no room sensor to regulate on. Gladys only
  // hands it the order of the preset: there is no setpoint to turn.
  isHeaterRegulated = () => {
    const cfg = this.state.remoteConfig;
    if (!cfg || !cfg.pilot_wire_feature) return false;
    return (
      cfg.thermostat_type === 'pilot-wire' ||
      cfg.thermostat_type === 'external' ||
      this.getTemperatureFeatures().length === 0
    );
  };

  // A pilot-wire heater Gladys regulates on the room sensor.
  isGladysRegulatedPilotWire = () => {
    const cfg = this.state.remoteConfig;
    return !!(cfg && cfg.pilot_wire_feature) && !this.isHeaterRegulated();
  };
  getMinTemp = () => {
    // Device feature native min has top priority
    if (this.state.featureMin !== null) return this.state.featureMin;
    const cfg = this.getConfig();
    if (cfg.temp_min !== undefined && cfg.temp_min !== null) return Number(cfg.temp_min);
    return DEFAULT_MIN_TEMP;
  };
  getMaxTemp = () => {
    // Device feature native max has top priority
    if (this.state.featureMax !== null) return this.state.featureMax;
    const cfg = this.getConfig();
    if (cfg.temp_max !== undefined && cfg.temp_max !== null) return Number(cfg.temp_max);
    return DEFAULT_MAX_TEMP;
  };

  // Effective temperature unit: device feature unit takes priority over user preference
  getEffectiveUnit = () => {
    if (this.state.featureUnit) return this.state.featureUnit;
    return (this.props.user && this.props.user.temperature_unit_preference) || DEVICE_FEATURE_UNITS.CELSIUS;
  };

  // Values from the device are in the feature's native unit; only when the feature has
  // no unit (assumed Celsius) and the user prefers Fahrenheit do we convert for display.
  needsFahrenheitConversion = () =>
    !this.state.featureUnit &&
    this.props.user &&
    this.props.user.temperature_unit_preference === DEVICE_FEATURE_UNITS.FAHRENHEIT;

  toDisplayTemp = temp => {
    if (temp === null || temp === undefined) return temp;
    return this.needsFahrenheitConversion() ? celsiusToFahrenheit(temp) : temp;
  };

  // The room sensor is a separate device from the thermostat, so it can report a
  // different unit — a Celsius Zigbee probe next to a Fahrenheit thermostat.
  // Everything downstream (the gauge, the "is it heating" hint) works in the
  // thermostat's unit, so the reading is brought into it here. The sensor unit
  // comes from the initial GET; websocket state events do not carry it.
  toThermostatUnit = (temp, sensorUnit) => {
    if (temp === null || temp === undefined) return temp;
    if (!sensorUnit) return temp;
    // Read from the instance field, not from getEffectiveUnit(): the thermostat
    // unit is stored through setState in the same pass and would still be stale.
    const thermostatUnit =
      this.thermostatUnit ||
      (this.props.user && this.props.user.temperature_unit_preference) ||
      DEVICE_FEATURE_UNITS.CELSIUS;
    if (sensorUnit === thermostatUnit) return temp;
    if (sensorUnit === DEVICE_FEATURE_UNITS.CELSIUS && thermostatUnit === DEVICE_FEATURE_UNITS.FAHRENHEIT) {
      return celsiusToFahrenheit(temp);
    }
    if (sensorUnit === DEVICE_FEATURE_UNITS.FAHRENHEIT && thermostatUnit === DEVICE_FEATURE_UNITS.CELSIUS) {
      return fahrenheitToCelsius(temp);
    }
    return temp;
  };

  // Get the temperature unit symbol
  getTempUnit = () => {
    return this.getEffectiveUnit() === DEVICE_FEATURE_UNITS.FAHRENHEIT ? 'F' : 'C';
  };

  getTemperatureFeatures = () => (this.state.remoteConfig && this.state.remoteConfig.temperature_features) || [];

  getWindowFeatures = () => (this.state.remoteConfig && this.state.remoteConfig.window_features) || [];

  // The room temperature is the average of the probes that have a value, the same
  // figure the server regulates on (spec A.1.1); a silent probe is left out
  // rather than counted as 0.
  setTemperatureReading = (selector, value) => {
    this.temperatureReadings[selector] =
      value === null || value === undefined ? null : this.toThermostatUnit(value, this.sensorUnits[selector]);
    const readings = this.getTemperatureFeatures()
      .map(feature => this.temperatureReadings[feature])
      .filter(reading => reading !== null && reading !== undefined);
    this.setState({
      currentTemp: readings.length ? readings.reduce((sum, reading) => sum + reading, 0) / readings.length : null
    });
  };

  // When each opening sensor opened, or null while it is closed. A contact
  // re-reporting "open" keeps the first time: the delay runs from the opening.
  setWindowReading = (selector, value, since = Date.now()) => {
    if (value !== 0) {
      this.openWindows[selector] = null;
    } else if (this.openWindows[selector] === null || this.openWindows[selector] === undefined) {
      this.openWindows[selector] = Math.min(since, Date.now());
    }
    this.showWindowBanner();
  };

  loadConfig = async () => {
    const remoteConfig = await loadDeviceConfig(this.props.httpClient, this.props.box.thermostat_feature);
    if (!remoteConfig) {
      return null;
    }
    await new Promise(resolve =>
      this.setState(prev => {
        // The device carries the mode this card wrote: nothing is pending any more.
        // A read that predates the write carries the old mode, and leaves it.
        const confirmed =
          remoteConfig.mode !== null &&
          remoteConfig.mode !== undefined &&
          Number(remoteConfig.mode) === prev.pendingMode;
        return confirmed ? { remoteConfig, pendingMode: null } : { remoteConfig };
      }, resolve)
    );
    return remoteConfig;
  };

  // The preset and the hold both arrive with the device: the preset as the
  // last_value of its feature, the hold as device params. There is nothing to
  // resolve and nothing to fetch — that is what moving them onto the device is
  // for. A thermostat that was never driven carries no preset, and the render
  // already handles that: writing a default here would make merely opening a
  // dashboard start the heating.
  loadMode = () => {
    const cfg = this.state.remoteConfig;
    if (!cfg) {
      return {};
    }
    // A stopped thermostat shows Off, whatever preset it carries underneath:
    // that preset is what it returns to, not what it is doing.
    const activePreset = this.isStopped() ? 'off' : this.followedPreset();
    const hold = this.getHold();
    return { activePreset, isManualMode: !this.isStopped() && !!hold };
  };

  // The preset the thermostat is actually on. `schedule` is not one of them: it
  // says "follow the programme", so what is in force is the programme's current
  // point — which the server sends alongside the schedule, already resolved.
  //
  // Reading it here rather than waiting for the regulation pass is what stops the
  // bar flashing: the pass is debounced by a couple of seconds, and until it ran
  // the widget would fall back on its default and light up Comfort.
  followedPreset = () => {
    const cfg = this.state.remoteConfig;
    const name = cfg ? PRESET_NAMES[cfg.preset] : null;
    if (name !== 'schedule') {
      return name || null;
    }
    const { activeSchedule, activePreset } = this.state;
    if (activeSchedule && activeSchedule.current) {
      return activeSchedule.current.preset;
    }
    // The schedule is not loaded yet, or carries no point. Keeping what is on
    // screen beats blanking it: a null preset hides the banner entirely, and the
    // widget would lose it for the time of a round trip.
    return activePreset || null;
  };

  // Stopped by hand: the machine is off, which outranks the programme. The
  // widget shows Off highlighted and the loop leaves the thermostat alone.
  isStopped = () => {
    const { remoteConfig: cfg, pendingMode } = this.state;
    const mode = pendingMode !== null ? pendingMode : cfg && cfg.mode;
    // A thermostat carries a mode, but it has no value until something writes
    // one, and an older device may predate the feature entirely. `Number(null)`
    // is 0, which is OFF, so the value has to be checked for being there before
    // it is compared: otherwise a thermostat that was never stopped reads as
    // permanently stopped.
    if (!cfg || mode === null || mode === undefined) {
      return false;
    }
    return Number(mode) === THERMOSTAT_MODE.OFF;
  };

  // The mode a running thermostat carries, from what it is configured to do.
  getRunningMode = () =>
    this.getConfig().default_mode === 'cooling' ? THERMOSTAT_MODE.COOLING : THERMOSTAT_MODE.HEATING;

  // Leaving a stop takes a mode write: the regulation loop skips a stopped
  // thermostat, so a setpoint or a preset written alone would be stored and
  // never applied. Every way out of Off goes through here.
  resumeIfStopped = async () => {
    if (!this.isStopped()) {
      return;
    }
    await this.writeMode(this.getRunningMode());
  };

  // The widget's own mode writes come back as a NEW_STATE on the mode feature.
  // Remembering the value lets that echo be told apart from a stop or a start
  // made elsewhere (a scene, the API, a second dashboard), which has to reload
  // the whole card.
  //
  // It comes back twice: the thermostat service saves the state, and so does the
  // core, which saves it itself for a feature without feedback. The value is
  // therefore remembered for a few seconds rather than for one event: the second
  // echo rebuilt the card from a device read taken before the preset and the
  // setpoint written next had landed, and the card ended on neither. Every value
  // written is remembered on its own: tapping Off then Comfort before the Off
  // echoes arrived took them for a stop made elsewhere, and the reload they
  // started left the card on Off while the thermostat heated.
  //
  // The mode is also held as pending from the tap, in the same render as
  // whatever the tap lit: the banner, the gauge and the event handlers read a
  // stop from it, in both directions, and none of them waits for the reload.
  // A failed write lets it go, and the device's mode shows again.
  writeMode = async value => {
    const cfg = this.state.remoteConfig;
    this.forgetExpectedMode(value);
    this.expectedModes.set(
      value,
      setTimeout(() => this.forgetExpectedMode(value), 5000)
    );
    this.setState({ pendingMode: value });
    const written = await this.writeFeature(cfg && cfg.modeFeature, value);
    if (!written) {
      // No echo is coming: a stop or a start made elsewhere must not be skipped.
      this.forgetExpectedMode(value);
      this.setState({ pendingMode: null });
    }
    return written;
  };

  // Forget one value the card wrote, or all of them.
  forgetExpectedMode = (value = undefined) => {
    const values = value === undefined ? [...this.expectedModes.keys()] : [value];
    values.forEach(candidate => {
      clearTimeout(this.expectedModes.get(candidate));
      this.expectedModes.delete(candidate);
    });
  };

  // The manual hold, as the device carries it. `until` is null on a permanent
  // hold, which is what a thermostat following no schedule gets.
  getHold = () => {
    const cfg = this.state.remoteConfig;
    if (!cfg || cfg.manual_setpoint === null || cfg.manual_setpoint === undefined) {
      return null;
    }
    const until = cfg.manual_until && cfg.manual_until > Date.now() ? cfg.manual_until : null;
    return { setpoint: cfg.manual_setpoint, until };
  };

  // Last preset that was not a stop, so turning the thermostat back on restores
  // what the user had. Stopping is a mode now, and a mode leaves the preset
  // feature untouched, so the preset the device carries *is* the fallback —
  // there is nothing left to store on the side.
  saveLastActivePreset = preset => {
    if (isStartPreset(preset)) {
      this.lastActivePreset = preset;
    }
  };

  // Read synchronously: the pointer/increment handlers need it during the same
  // tick to stay responsive. Only a named preset: `schedule` names no
  // temperature to start on, and writing it hands the thermostat back to its
  // programme. A thermostat stopped while following one restarts on Comfort.
  getLastActivePreset = () => {
    const cfg = this.state.remoteConfig;
    const carried = cfg && PRESET_NAMES[cfg.preset];
    return this.lastActivePreset || (isStartPreset(carried) ? carried : null) || 'comfort';
  };

  // Every write goes through the generic feature value route, the one the whole
  // of Gladys already uses: picking a preset is a value on the preset feature,
  // holding a temperature a value on target-temperature, stopping a value on
  // mode. The widget owns no state machine — the server does — so there is
  // nothing here to keep in step with it.
  writeFeature = async (feature, value) => {
    if (!feature) return false;
    try {
      await this.props.httpClient.post(`/api/v1/device_feature/${feature.selector}/value`, { value });
      return true;
    } catch (e) {
      console.error('Failed to write a thermostat feature:', e);
      return false;
    }
  };

  savePreset = async preset => {
    const cfg = this.state.remoteConfig;
    const value = PRESET_VALUES[preset];
    if (!cfg || !cfg.presetFeature || value === undefined) {
      return;
    }
    this.savingPreset = true;
    // Remembered past the write: its echo can land after the response, and in
    // manual mode it must not be taken for a preset picked elsewhere.
    this.lastPresetWrite = { preset, at: Date.now() };
    try {
      await this.writeFeature(cfg.presetFeature, value);
    } finally {
      this.savingPreset = false;
    }
  };

  // Comfort is amber when heating and blue when cooling, like the arc and the
  // preset icon: an air conditioner labelled in the colour of heat reads wrong.
  isDarkTheme = () =>
    (typeof document !== 'undefined' && document.documentElement.classList.contains('dark-mode')) ||
    Boolean(this.props.darkMode);

  getPresetColor = presetKey => {
    const cfg = this.getConfig();
    return presetColorForMode(presetKey, cfg.default_mode);
  };

  getPresets = () => {
    const cfg = this.getConfig();
    const mode = cfg.default_mode || 'heating';
    const keys = mode === 'cooling' ? COOLING_PRESETS : HEATING_PRESETS;
    const defaults = defaultPresetTemps(mode);
    const allPresets = {
      off: { key: 'off', icon: PRESET_ICONS.off, temp: null },
      frost: { key: 'frost', icon: PRESET_ICONS.frost, temp: numOr(cfg.preset_frost, defaults.frost) },
      away: { key: 'away', icon: PRESET_ICONS.away, temp: numOr(cfg.preset_away, defaults.away) },
      comfort: {
        key: 'comfort',
        icon: mode === 'cooling' ? COMFORT_ICON.cooling : COMFORT_ICON.heating,
        temp: numOr(cfg.preset_comfort, defaults.comfort)
      },
      eco: { key: 'eco', icon: PRESET_ICONS.eco, temp: numOr(cfg.preset_eco, defaults.eco) },
      night: { key: 'night', icon: PRESET_ICONS.night, temp: numOr(cfg.preset_night, defaults.night) }
    };
    return keys.map(k => allPresets[k]);
  };

  // An opening only suspends the heating once it has stayed open
  // THERMOSTAT_WINDOW_DELAY seconds, and the banner says so no sooner: shown at
  // the opening, it announced a cut that a door closed right behind never got.
  // The count runs from the opening this card knows of — the feature's
  // `last_value_changed` on load, the NEW_STATE afterwards. Any of the sensors
  // open long enough suspends the heating, as on the server (spec A.1.1), and
  // the card checks again when the first one still counting gets there.
  showWindowBanner = () => {
    if (this.windowTimer) {
      clearTimeout(this.windowTimer);
      this.windowTimer = null;
    }
    // Bounded like the server's: a value stored through another route, or before the
    // bound existed, overflowed the timer and showed the banner at once.
    const delay = Math.min(
      MAX_WINDOW_DELAY_SECONDS,
      Math.max(0, numOr(this.getConfig().window_delay, DEFAULT_WINDOW_DELAY_SECONDS))
    );
    const now = Date.now();
    const remaining = this.getWindowFeatures()
      .map(feature => this.openWindows[feature])
      .filter(openedAt => openedAt !== null && openedAt !== undefined)
      .map(openedAt => openedAt + delay * 1000 - now);
    const isWindowOpen = remaining.some(left => left <= 0);
    this.setState({ isWindowOpen });
    if (!isWindowOpen && remaining.length > 0) {
      this.windowTimer = setTimeout(() => {
        this.windowTimer = null;
        this.showWindowBanner();
      }, Math.min(...remaining));
    }
  };

  getDeviceData = async () => {
    const { box } = this.props;
    const thermostatFeature = box.thermostat_feature;
    // temperature/humidity/window features come from integration config (remoteConfig), not box props
    const temperatureFeatures = this.getTemperatureFeatures();
    const humidityFeature = (this.state.remoteConfig && this.state.remoteConfig.humidity_feature) || null;
    const windowFeatures = this.getWindowFeatures();
    // The running state comes from the switch Gladys drives on a virtual
    // thermostat, and from the real device's state feature on an external one.
    // Both end up in `isSwitchOn`, which is what draws the heating halo.
    const switchFeature = this.getRunningStateFeature();
    const pilotWireFeature = this.isHeaterRegulated() ? this.state.remoteConfig.pilot_wire_feature : null;
    if (!thermostatFeature && temperatureFeatures.length === 0) {
      this.setState({ noConfig: true });
      return;
    }
    // Reset features that have been removed
    const stateUpdate = { noConfig: false, error: false };
    if (temperatureFeatures.length === 0) stateUpdate.currentTemp = null;
    if (!humidityFeature) stateUpdate.humidity = null;
    this.temperatureReadings = {};
    // A sensor still configured keeps the time it opened: `last_value_changed`
    // moves on every report, and read again here it would restart the count.
    this.openWindows = windowFeatures.reduce(
      (kept, feature) => ({ ...kept, [feature]: this.openWindows[feature] }),
      {}
    );
    if (windowFeatures.length === 0) {
      // No sensor left: no banner, nor a check still waiting to show one.
      this.showWindowBanner();
    }
    if (!switchFeature) stateUpdate.isSwitchOn = null;
    if (!pilotWireFeature) stateUpdate.pilotWireOrder = null;
    this.setState(stateUpdate);
    const selectors = [
      thermostatFeature,
      ...temperatureFeatures,
      humidityFeature,
      ...windowFeatures,
      switchFeature,
      pilotWireFeature
    ]
      .filter(Boolean)
      .join(',');
    if (!selectors) return;
    try {
      const devices = await this.props.httpClient.get('/api/v1/device', {
        device_feature_selectors: selectors
      });
      if (devices && devices.length) {
        // Both units must be known before any reading is converted: the two
        // features can arrive in any order, and setState is asynchronous, so
        // resolving them inside the loop would convert the first reading against
        // a stale unit.
        const allFeatures = devices.reduce((acc, device) => acc.concat(device.features || []), []);
        const thermostatUnitFeature = allFeatures.find(feat => feat.selector === thermostatFeature);
        if (thermostatUnitFeature && thermostatUnitFeature.unit) {
          this.thermostatUnit = thermostatUnitFeature.unit;
        }
        this.sensorUnits = {};
        allFeatures
          .filter(feat => temperatureFeatures.includes(feat.selector))
          .forEach(feat => {
            this.sensorUnits[feat.selector] = feat.unit || null;
          });

        devices.forEach(device => {
          device.features.forEach(feat => {
            if (feat.selector === thermostatFeature) {
              if (feat.last_value !== null && feat.last_value !== undefined) {
                // During manual mode, keep the manual setpoint
                // Also skip if setpoint was already set from schedule preset (avoid stale DB value)
                if (!this.state.isManualMode && !this._scheduleSetpointSet) {
                  this.setState({ setpoint: feat.last_value });
                }
              }
              this._scheduleSetpointSet = false;
              // Store native feature min/max/unit
              if (feat.min !== undefined && feat.min !== null) this.setState({ featureMin: feat.min });
              if (feat.max !== undefined && feat.max !== null) this.setState({ featureMax: feat.max });
              if (feat.unit) this.setState({ featureUnit: feat.unit });
            }
            if (temperatureFeatures.includes(feat.selector)) {
              this.setTemperatureReading(feat.selector, feat.last_value);
            }
            if (
              humidityFeature &&
              feat.selector === humidityFeature &&
              feat.last_value !== null &&
              feat.last_value !== undefined
            ) {
              this.setState({ humidity: feat.last_value });
            }
            if (windowFeatures.includes(feat.selector) && feat.last_value !== null && feat.last_value !== undefined) {
              // `new Date(null)` is the epoch: an undated change would show the
              // banner at once.
              const changed =
                feat.last_value_changed === null || feat.last_value_changed === undefined
                  ? NaN
                  : new Date(feat.last_value_changed).getTime();
              this.setWindowReading(feat.selector, feat.last_value, Number.isFinite(changed) ? changed : Date.now());
            }
            if (
              switchFeature &&
              feat.selector === switchFeature &&
              feat.last_value !== null &&
              feat.last_value !== undefined
            ) {
              this.stateFeature = feat;
              this.setState({ isSwitchOn: isRunningFromStateFeature(feat, feat.last_value) });
            }
            if (pilotWireFeature && feat.selector === pilotWireFeature) {
              this.setState({
                pilotWireOrder: feat.last_value,
                pilotWireSupportedOptions: feat.supported_options || null
              });
            }
          });
        });
      }
    } catch (e) {
      console.error(e);
      this.setState({ error: true });
    }
  };

  handleWebsocketMessage = payload => {
    const { box } = this.props;
    const thermostatFeature = box.thermostat_feature;
    const humidityFeature = (this.state.remoteConfig && this.state.remoteConfig.humidity_feature) || null;
    if (thermostatFeature && payload.device_feature_selector === thermostatFeature) {
      // Don't overwrite a setpoint the user is holding here. `isManualMode` is
      // not enough on its own: a scene writing the setpoint puts the device in
      // manual mode too, and its MANUAL_MODE_UPDATED often lands before the
      // NEW_STATE carrying the new value — the guard would then swallow the very
      // event that was supposed to display it, leaving the old setpoint on screen
      // until the next refresh. `manualSetpointOverride` is set only by this
      // widget's own dial and buttons, so it tells the two apart.
      // On an external thermostat the real device is a second source of truth:
      // turning the dial on the Netatmo itself, or a change made in its own app,
      // arrives here as a NEW_STATE and has to be displayed. Holding the local
      // setpoint would leave the widget showing a value the thermostat no longer
      // has. A virtual thermostat has no such second source, so its own manual
      // hold still wins there.
      if (this.isExternal() || !this.state.isManualMode || !this.state.manualSetpointOverride) {
        // Just left manual mode: drop the in-flight events carrying the old
        // manual setpoint, and only resume following the device once the value
        // we just applied comes back.
        if (this.expectedSetpoint !== null && this.expectedSetpoint !== undefined) {
          // Held: only the value we applied ourselves lifts the hold. While the
          // target is still unknown (HOLD_ANY_SETPOINT), every value is dropped.
          if (payload.last_value === this.expectedSetpoint) {
            this.releaseSetpointHold();
            this.setState({ setpoint: payload.last_value });
          }
        } else {
          this.setState({ setpoint: payload.last_value });
        }
      }
    }
    if (this.getTemperatureFeatures().includes(payload.device_feature_selector)) {
      this.setTemperatureReading(payload.device_feature_selector, payload.last_value);
    }
    if (humidityFeature && payload.device_feature_selector === humidityFeature) {
      this.setState({ humidity: payload.last_value });
    }
    if (this.getWindowFeatures().includes(payload.device_feature_selector)) {
      // The server (onDeviceNewState) cuts the switch; the widget only reflects the state
      this.setWindowReading(payload.device_feature_selector, payload.last_value);
    }
    const modeFeature = this.state.remoteConfig && this.state.remoteConfig.modeFeature;
    if (modeFeature && payload.device_feature_selector === modeFeature.selector) {
      this.handleModeChanged(payload.last_value);
    }
    const switchFeature = this.getRunningStateFeature();
    if (switchFeature && payload.device_feature_selector === switchFeature) {
      // NEW_STATE carries no category/type, so the shape cached from the initial
      // GET /api/v1/device is what the value is normalised against.
      this.setState({ isSwitchOn: isRunningFromStateFeature(this.stateFeature, payload.last_value) });
    }
    const remoteConfig = this.state.remoteConfig;
    if (
      this.isHeaterRegulated() &&
      remoteConfig &&
      payload.device_feature_selector === remoteConfig.pilot_wire_feature
    ) {
      this.setState({ pilotWireOrder: payload.last_value });
    }
  };

  // A stop or a start made outside this card changes everything it shows: the
  // gauge, the lit preset, the banner. The card is rebuilt the way a reload
  // builds it, rather than patched. Its own writes are skipped, both echoes of
  // them (see writeMode): the click that made them already put the card in the
  // right state.
  handleModeChanged = value => {
    if (this.expectedModes.has(Number(value))) {
      return;
    }
    this.setState({ pendingMode: null });
    this.initData();
  };

  handleWebsocketConnected = ({ connected }) => {
    if (!connected) {
      this.wasDisconnected = true;
    } else if (this.wasDisconnected) {
      this.getDeviceData();
      this.wasDisconnected = false;
    }
  };

  // The configuration lives on the device, so the event carries none: it only
  // says "reload it". Reading a copy out of the payload would be a second store
  // that could disagree with the device the regulation loop actually reads.
  handleThermostatConfigUpdated = async () => {
    if (!this.props.box.thermostat_feature) return;
    await this.loadConfig();
    // The schedule before the state that reads it: a thermostat following its
    // programme takes its preset from the point in force.
    await this.loadSchedule();
    const { activePreset, isManualMode } = this.loadMode();
    this.setState({ activePreset, isManualMode });
    await this.getDeviceData();
    this.applyFallbackSetpoint();
  };

  // The server names the thermostat, not a variable key: the preset is a feature
  // of that device now.
  isOurDevice = payload => {
    const cfg = this.state.remoteConfig;
    return !!cfg && !!payload && payload.device === cfg.device_selector;
  };

  handleThermostatPresetUpdated = payload => {
    if (!this.isOurDevice(payload) || this.savingPreset) return;
    // A preset set by a scene or the API on a stopped thermostat is stored, not
    // applied: the loop skips a stopped thermostat. Taking the event as is drew
    // the preset's setpoint and lit its button, as if the heater were running.
    // Reloading the device keeps Off and shows the preset as the one it will
    // start back on.
    if (this.isStopped()) {
      this.refreshFromDevice();
      return;
    }
    const knownPresets = [...HEATING_PRESETS, ...COOLING_PRESETS];
    if (!payload.preset || !knownPresets.includes(payload.preset)) return;
    const resolvedPreset = payload.preset;
    // A preset event never clears an active hold: the server only pushes a preset
    // while none is armed, just as one expires — the hold event then does the
    // clearing — or when a scene or the API picks one, which arms that preset's
    // own hold in the same write. The card then shows that preset on its
    // temperature: keeping the override of a setpoint held here left it on
    // "21.5 · Manual mode", swallowing the preset's setpoint, until a reload.
    const manual = this.state.isManualMode;
    const own = this.lastPresetWrite;
    if (manual && own && own.preset === resolvedPreset && Date.now() - own.at < 5000) {
      // This card's own pick, whose hold it already shows.
      this.setState({ activePreset: resolvedPreset });
      return;
    }
    const newState = manual
      ? { activePreset: resolvedPreset, manualSetpointOverride: false }
      : { activePreset: resolvedPreset, isManualMode: false, manualSetpointOverride: false };
    if (resolvedPreset !== 'off') {
      const presets = this.getPresets();
      const preset = presets.find(p => p.key === resolvedPreset);
      if (preset && preset.temp !== null && preset.temp !== undefined) {
        newState.setpoint = this.toSetpointFeatureUnit(preset.temp);
      }
    }
    if (!manual) this.loadSchedule();
    this.setState(newState);
  };

  handleThermostatManualModeUpdated = payload => {
    if (!this.isOurDevice(payload)) return;
    // The same preset write also arms a hold, which only takes effect once the
    // thermostat starts again: a stopped one is in no manual mode.
    if (this.isStopped()) return;
    // A hold with no setpoint is a cleared hold.
    const isManual = payload.setpoint !== null && payload.setpoint !== undefined;
    // On an external thermostat this event also announces a setpoint changed on
    // the device itself (its dial, the vendor app, its own programme): the
    // setpoint no longer comes from the preset, so the preset must stop being
    // shown as active. `manualSetpointOverride` is what un-highlights it, and
    // only this widget's own dial and buttons would otherwise set it.
    //
    // This cannot hang off a *change* of isManualMode: without a schedule the
    // hold is permanent, so a thermostat already in manual mode stays in it, and
    // every later change on the device re-emits `true` with nothing to compare.
    // Same rule as on a reload: a hold landing on the active preset's own setpoint
    // is that preset being applied, and the button stays lit. Forcing the override
    // for every external hold — on the grounds that a manual setpoint never comes
    // from a preset on a real device — left a preset picked on a Netatmo dark
    // until the page was reloaded, while the banner read "Manual mode".
    if (
      isManual &&
      !this.savingPreset &&
      !this.pickingPreset &&
      this.isExternal() &&
      !this.holdMatchesPreset(payload.setpoint, this.state.activePreset)
    ) {
      this.setState({ manualSetpointOverride: true });
    }
    // And the other way round, on any thermostat: a hold landing on the active
    // preset's own setpoint is that preset applied. A scene picking the preset
    // the thermostat already carries sends no preset event, only this one, and
    // the card kept "21.5 · Manual mode" from its own dial until a reload.
    if (
      isManual &&
      !this.savingPreset &&
      !this.pickingPreset &&
      !this.state.isDragging &&
      this.state.manualSetpointOverride &&
      this.holdMatchesPreset(payload.setpoint, this.state.activePreset)
    ) {
      this.setState({ manualSetpointOverride: false, setpoint: payload.setpoint });
    }
    if (!isManual && this.state.isManualMode) {
      // The server let the hold expire — the schedule takes the thermostat back.
      // Hold the setpoint first: see cancelManualMode for why.
      this.holdSetpointUntilApplied();
      this.setState({ isManualMode: false, manualUntil: null, manualSetpointOverride: false });
      this.refreshFromDevice();
    } else if (isManual !== this.state.isManualMode && !this.savingPreset) {
      this.setState({ isManualMode: isManual });
    } else if (isManual && payload.until) {
      // The server's expiry always wins over the one shown meanwhile: it is the
      // moment the loop will actually hand the thermostat back. The widget can
      // only guess at a fixed duration, and a hold that runs to the next schedule
      // point ends whenever that point is — which the widget cannot work out
      // without the whole week in the right timezone.
      //
      // This also covers a hold taken with no schedule, which carries no expiry:
      // the banner falls back to the schedule one, which has no cancel button,
      // until the server arms the expiry and sends it here.
      if (payload.until > Date.now()) {
        this.setState({ manualUntil: payload.until });
      }
    }
  };

  // Re-read the device and adopt what it carries. Everything the widget shows is
  // state of the device now, so one reload replaces the handful of round-trips
  // this used to take.
  refreshFromDevice = async () => {
    // The schedule first: a thermostat following it takes its preset from the
    // point in force, which loadMode reads out of it.
    await this.loadSchedule();
    await this.loadConfig();
    const { activePreset, isManualMode } = this.loadMode();
    this.setState({ activePreset, isManualMode });
  };

  // Which schedule a thermostat follows is a relation, so the server is asked
  // rather than a device param read: the schedule that lists this thermostat is
  // the one it follows. It comes back with `current` and `next` already resolved
  // in the Gladys timezone, so the widget never reads a timezone and never
  // recomputes a point — a phone abroad would otherwise name a point other than
  // the one actually heating the house.
  loadSchedule = async () => {
    const cfg = this.state.remoteConfig;
    const deviceSelector = cfg && cfg.device_selector;
    if (!deviceSelector) {
      this.setState({ activeSchedule: null });
      return;
    }
    try {
      const schedules = await this.props.httpClient.get('/api/v1/service/thermostat/schedule');
      const schedule = (schedules || []).find(candidate =>
        (candidate.devices || []).some(device => device.selector === deviceSelector)
      );
      // A schedule deleted behind the widget's back is missing from the list,
      // and degrades to "no schedule" rather than leaving a stale banner.
      this.setState({ activeSchedule: schedule || null });
    } catch (e) {
      // A failed request says nothing about the schedule: the last one loaded
      // stays, and with it its `next.refresh_at`, so the minute tick tries again
      // instead of dropping the banner for good.
    }
  };

  // Handing the thermostat back to its programme is a single write: `schedule`
  // on the preset feature. The server clears the hold and regulates on the
  // programme from there — the widget does not have to resolve which preset that
  // is, nor clear anything itself.
  // Hand the thermostat back to its programme, from a hold or from a stop. A
  // stopped thermostat is skipped by the regulation loop, so the mode has to be
  // written back first or the preset would be stored and never applied.
  // Start it again, without touching the preset. `cancelManualMode` hands the
  // thermostat back to its programme, which a thermostat following none does not
  // have: it would store a `schedule` preset nothing can act on.
  //
  // The card is rebuilt the way a reload builds it: the thermostat starts on the
  // preset and the hold it carried while stopped, and only initData works out
  // which button that lights and which banner it shows. Reloading the preset
  // alone left every button dark until the page was reloaded.
  //
  // The mode turns running at the tap (see writeMode), so the preset it starts
  // back on is lit with it: left on Off, the card read as a running thermostat
  // on Off until initData had made its round trips.
  resumeFromStopped = async () => {
    const cfg = this.state.remoteConfig;
    const carried = cfg && PRESET_NAMES[cfg.preset];
    this.setState({ activePreset: isStartPreset(carried) ? carried : null });
    await this.resumeIfStopped();
    await this.initData();
  };

  cancelManualMode = async () => {
    this.holdSetpointUntilApplied();
    // The point in force is what the thermostat follows from the tap on, and the
    // server resolved it already: lit now, with the mode a stop hands back, not
    // once the reload below has landed — until then the card read as a running
    // thermostat still on Off.
    const current = this.state.activeSchedule && this.state.activeSchedule.current;
    this.setState({
      isManualMode: false,
      manualUntil: null,
      manualSetpointOverride: false,
      ...(current ? { activePreset: current.preset } : {})
    });
    await this.resumeIfStopped();
    await this.savePreset('schedule');
    // The schedule first: its `current` point is what the thermostat follows
    // from now on, and loadMode reads it.
    await this.loadSchedule();
    await this.loadConfig();
    const { activePreset } = this.loadMode();
    // Every setpoint was dropped since the tap, the server's own included: it
    // lands within a second or two, so the card kept the held 20 °C under
    // "Comfort" until a reload (forum 11074). The point's setpoint is shown now,
    // and only the value it writes lifts the hold — or nothing, on a point that
    // writes none.
    const preset = this.getPresets().find(candidate => candidate.key === activePreset);
    if (preset && preset.temp !== null && preset.temp !== undefined) {
      const setpoint = this.toSetpointFeatureUnit(preset.temp);
      this.holdSetpointUntilApplied(setpoint);
      this.setState({ activePreset, setpoint });
    } else {
      this.releaseSetpointHold();
      this.setState({ activePreset });
    }
  };

  // Ignore incoming device setpoints until the one we are about to apply lands.
  // Passing no value holds every value until the target is known.
  holdSetpointUntilApplied = (value = undefined) => {
    this.expectedSetpoint = value === undefined ? HOLD_ANY_SETPOINT : value;
    if (this.expectedSetpointTimer) {
      clearTimeout(this.expectedSetpointTimer);
    }
    // Safety net: never stay deaf to device updates if that event never lands.
    this.expectedSetpointTimer = setTimeout(() => {
      this.expectedSetpoint = null;
      this.expectedSetpointTimer = null;
    }, 10000);
  };

  releaseSetpointHold = () => {
    this.expectedSetpoint = null;
    if (this.expectedSetpointTimer) {
      clearTimeout(this.expectedSetpointTimer);
      this.expectedSetpointTimer = null;
    }
  };

  // The server arms the hold and its expiry; this only fills the countdown while
  // the event that carries the real one is in flight, and only when the device
  // configures a fixed duration — the same figure the server will apply.
  //
  // With no duration configured the hold runs to the schedule's next point
  // instead, which is not a figure the widget can work out: it would need the
  // whole week resolved in the Gladys timezone. Showing +30 min there displayed
  // a time the thermostat never came back at, and replaced it on the next reload.
  // Nothing is shown until the server says when.
  showManualCountdown = () => {
    const cfg = this.getConfig();
    const duration = numOr(cfg.manual_duration, null);
    if (duration === null) {
      return;
    }
    this.setState({ manualUntil: Date.now() + duration * 60 * 1000 });
  };

  // Whether a hold is a preset being applied, rather than a setpoint of its own.
  // A hold alone says nothing: picking a preset arms one exactly as turning the
  // dial does. A hold sitting on the preset's own setpoint *is* that preset and
  // keeps it highlighted; any other value is a setpoint no preset represents —
  // which is what a temperature set on the real thermostat looks like.
  //
  // Extracted because the reload path and the live event have to answer this the
  // same way. They did not: `initData` applied the rule and the websocket handler
  // still forced the override on every external hold, so a preset picked on a
  // Netatmo stayed dark until the page was reloaded.
  //
  // The hold is stored in the unit of the setpoint feature (C.3), the preset in
  // the thermostat's: Comfort at 21 °C is held as 70 on a Fahrenheit appliance.
  // The preset's temperature is converted the way the server converts it before
  // the two are compared, or a preset picked on such a device never lit.
  holdMatchesPreset = (setpoint, presetKey) => {
    if (setpoint === null || setpoint === undefined || !presetKey) {
      return false;
    }
    return this.getPresets().some(
      candidate =>
        candidate.key === presetKey &&
        candidate.temp !== null &&
        this.toSetpointFeatureUnit(candidate.temp) === setpoint
    );
  };

  // The preset whose temperature a setpoint lands on, or null. Asked on the forum:
  // a temperature set by hand that is exactly Comfort's reads as Comfort, so the
  // button lights instead of the bar going dark under "Manual mode". Display
  // only — the server still holds a manual setpoint, which expires like any
  // other. Two presets on the same temperature light the first in bar order.
  presetMatchingSetpoint = setpoint => {
    if (setpoint === null || setpoint === undefined) {
      return null;
    }
    const match = this.getPresets().find(
      candidate => candidate.temp !== null && this.toSetpointFeatureUnit(candidate.temp) === setpoint
    );
    return match ? match.key : null;
  };

  // Same rounding as the server's convertSetpointToFeatureUnit: a whole degree
  // Fahrenheit, half a degree Celsius. Only an external thermostat's hold is
  // converted there: a virtual one's setpoint feature is in its own unit.
  toSetpointFeatureUnit = temp => {
    if (!this.isExternal()) return temp;
    const { featureUnit } = this.state;
    const thermostatUnit = this.getConfig().temp_unit || 'C';
    if (featureUnit === DEVICE_FEATURE_UNITS.FAHRENHEIT && thermostatUnit === 'C') {
      return Math.round(celsiusToFahrenheit(temp));
    }
    if (featureUnit === DEVICE_FEATURE_UNITS.CELSIUS && thermostatUnit === 'F') {
      return Math.round(fahrenheitToCelsius(temp) * 2) / 2;
    }
    return temp;
  };

  initData = async () => {
    await this.loadConfig();
    // The schedule before the state that reads it: a thermostat following its
    // programme takes its preset from the point in force, and the banner needs
    // the schedule to exist at all.
    await this.loadSchedule();
    const { activePreset, isManualMode } = this.loadMode();

    // Build initial state update: apply preset and manual mode atomically,
    // then restore the manual setpoint if it is still active.
    // This must be committed BEFORE getDeviceData() so the setpoint guard works.
    // Reset rather than left as they were: initData also rebuilds a card that is
    // already on screen (a stop or a start made elsewhere), and a stale override
    // would keep every preset dark.
    const stateInit = { manualSetpointOverride: false, manualUntil: null };
    if (activePreset !== null) stateInit.activePreset = activePreset;
    if (isManualMode !== null) stateInit.isManualMode = isManualMode;

    // If not in manual mode and a preset was resolved, apply its setpoint immediately
    // so the gauge shows the correct temperature without waiting for getDeviceData
    let scheduledTemp = null;
    if (!isManualMode && activePreset && activePreset !== 'off') {
      const presets = this.getPresets();
      const presetObj = presets.find(p => p.key === activePreset);
      if (presetObj && presetObj.temp !== null && presetObj.temp !== undefined) {
        scheduledTemp = presetObj.temp;
        stateInit.setpoint = this.toSetpointFeatureUnit(scheduledTemp);
        this._scheduleSetpointSet = true;
      }
    }

    // The hold came with the device, so there is nothing left to fetch here.
    // A stopped thermostat can carry a hold — a preset written while it was off
    // arms one — but it only takes effect once it starts again. Reading it here
    // marked the setpoint as manual, and Off was no longer lit.
    const hold = this.isStopped() ? null : this.getHold();
    if (hold) {
      stateInit.setpoint = hold.setpoint;
      // Every preset the user picks arms a hold, so a hold alone says nothing
      // about where the setpoint came from. A hold sitting exactly on the
      // preset's own setpoint is that preset being applied — keep it
      // highlighted, or a reload (or a second device, or a wall tablet) would
      // show no active preset at all on a thermostat that plainly has one. A
      // hold on any other value is a setpoint of its own, which no preset
      // represents.
      //
      // This is the rule for an external thermostat too. It used to be skipped
      // there, on the grounds that a manual setpoint never comes from a preset
      // on a real device — but picking a preset in the widget arms a hold there
      // exactly as it does on a virtual one, so no preset ever lit up on a
      // Netatmo. A setpoint genuinely turned on the device lands on a value no
      // preset carries and is still read as manual, which is the case that
      // reasoning was actually about.
      if (!this.holdMatchesPreset(hold.setpoint, activePreset)) {
        stateInit.manualSetpointOverride = true;
      }
      if (hold.until) {
        stateInit.manualUntil = hold.until;
      }
    }

    // Commit everything atomically and wait for the state to be applied
    await new Promise(resolve => this.setState(stateInit, resolve));
    await this.getDeviceData();
    // The setpoint feature's unit is only known once its device is read: on the
    // first load the hold was compared with the preset, and the programme's
    // setpoint converted, in the thermostat's unit — and the feature's own value
    // was skipped for the latter, so 21 stayed on screen under °F.
    if (hold && stateInit.manualSetpointOverride && this.holdMatchesPreset(hold.setpoint, activePreset)) {
      this.setState({ manualSetpointOverride: false });
    }
    if (!hold && scheduledTemp !== null && this.state.setpoint !== this.toSetpointFeatureUnit(scheduledTemp)) {
      this.setState({ setpoint: this.toSetpointFeatureUnit(scheduledTemp) });
    }
    this.applyFallbackSetpoint();
    if (!this.state.setpointLoaded) {
      // The corrections above, and getDeviceData's, are drawn first, still jumping: set
      // in the same turn, the flag was flushed in the same render, and the gauge glided
      // 21 → 70 on a °F feature all the same.
      await new Promise(resolve => this.setState({}, resolve));
      this.setState({ setpointLoaded: true });
    }
  };

  // A thermostat that has never been driven has no setpoint anywhere: its
  // target-temperature feature was created empty and no PRESET was ever stored.
  // Without this the render finds no error, no missing config and no setpoint,
  // and draws an empty card. Show the gauge on the comfort temperature instead —
  // purely local, nothing is written to the device, so the widget stays a
  // proposal until the user turns the dial or picks a preset.
  applyFallbackSetpoint = () => {
    const { setpoint, noConfig, error } = this.state;
    if (setpoint !== null && setpoint !== undefined) return;
    if (noConfig || error || !this.props.box.thermostat_feature) return;
    const cfg = this.getConfig();
    const comfort = numOr(cfg.preset_comfort, defaultPresetTemps(cfg.default_mode).comfort);
    // The comfort preset can sit outside the device's own range, which would
    // put the needle off the arc.
    const fallback = Math.min(this.getMaxTemp(), Math.max(this.getMinTemp(), comfort));
    this.setState({ setpoint: fallback });
  };

  // Local minute tick. The current point is resolved by the server, so this only
  // clears the countdown when it visually expires; the server's own event is
  // what actually ends the hold. The days the banners name go stale on their
  // own, with nothing else to draw the card again: a wall panel still said
  // "until tomorrow 06:30" at 03:00. The hold banner counts in the phone's day,
  // drawn again when that day changes; the programme banner's count is the
  // server's, loaded again once it says it no longer holds.
  refreshClock = () => {
    const { manualUntil, activeSchedule, today } = this.state;
    const now = Date.now();
    if (manualUntil && now > manualUntil) {
      this.setState({ manualUntil: null });
    } else if (manualUntil && today !== new Date(now).toDateString()) {
      this.setState({ today: new Date(now).toDateString() });
    }
    const next = activeSchedule && activeSchedule.next;
    if (next && next.refresh_at && now >= next.refresh_at) {
      this.loadSchedule();
    }
  };

  componentDidMount() {
    this.initData();
    this.clockInterval = setInterval(this.refreshClock, 60 * 1000);
    this.props.session.dispatcher.addListener(WEBSOCKET_MESSAGE_TYPES.DEVICE.NEW_STATE, this.handleWebsocketMessage);
    this.props.session.dispatcher.addListener(
      WEBSOCKET_MESSAGE_TYPES.THERMOSTAT.CONFIG_UPDATED,
      this.handleThermostatConfigUpdated
    );
    this.props.session.dispatcher.addListener(
      WEBSOCKET_MESSAGE_TYPES.THERMOSTAT.PRESET_UPDATED,
      this.handleThermostatPresetUpdated
    );
    this.props.session.dispatcher.addListener(
      WEBSOCKET_MESSAGE_TYPES.THERMOSTAT.MANUAL_MODE_UPDATED,
      this.handleThermostatManualModeUpdated
    );
    this.props.session.dispatcher.addListener('websocket.connected', this.handleWebsocketConnected);
  }

  componentWillUnmount() {
    clearInterval(this.clockInterval);
    // A drag in progress keeps window-level listeners alive: unmounting
    // mid-drag (dashboard edit, tab switch) would leak them. The gesture never
    // reached pointer-up, so nothing was persisted and there is nothing to
    // undo — the device keeps whatever mode it had before the drag started.
    this.stopDrag();
    if (this.expectedSetpointTimer) {
      clearTimeout(this.expectedSetpointTimer);
      this.expectedSetpointTimer = null;
    }
    if (this.windowTimer) {
      clearTimeout(this.windowTimer);
      this.windowTimer = null;
    }
    this.forgetExpectedMode();
    this.props.session.dispatcher.removeListener(WEBSOCKET_MESSAGE_TYPES.DEVICE.NEW_STATE, this.handleWebsocketMessage);
    this.props.session.dispatcher.removeListener(
      WEBSOCKET_MESSAGE_TYPES.THERMOSTAT.CONFIG_UPDATED,
      this.handleThermostatConfigUpdated
    );
    this.props.session.dispatcher.removeListener(
      WEBSOCKET_MESSAGE_TYPES.THERMOSTAT.PRESET_UPDATED,
      this.handleThermostatPresetUpdated
    );
    this.props.session.dispatcher.removeListener(
      WEBSOCKET_MESSAGE_TYPES.THERMOSTAT.MANUAL_MODE_UPDATED,
      this.handleThermostatManualModeUpdated
    );
    this.props.session.dispatcher.removeListener('websocket.connected', this.handleWebsocketConnected);
  }

  componentDidUpdate(prevProps) {
    const { box } = this.props;
    // thermostat_feature is the only device-referencing field left on the box:
    // every regulation setting, the active schedule included, lives on the device
    // and arrives through loadConfig / CONFIG_UPDATED.
    if (prevProps.box.thermostat_feature !== box.thermostat_feature) {
      // initData is the one ordering that works: the config must be loaded before
      // the mode is derived from it, and the preset/manual values it returns have
      // to be committed. Calling the four loaders side by side raced on all three.
      this.initData();
    }
    // Switch actuation is handled exclusively by the server (applySchedules,
    // triggered every minute and debounced after each variable/setpoint change).
  }

  // The setpoint route treats a write as a manual override, like a scene would.
  // Pass manual: false when writing back the setpoint the schedule dictates —
  // otherwise returning to the schedule immediately re-arms the override it is
  // clearing, and the widget shows the schedule while the database says manual.
  // Writing a temperature is a value on the setpoint feature. The server turns it
  // into a hold, with the expiry the device's own settings say — the widget does
  // not arm anything, and does not have to stay in step with what it armed.
  sendSetpoint = async value => {
    const { box } = this.props;
    if (!box.thermostat_feature) return;
    try {
      await this.props.httpClient.post(`/api/v1/device_feature/${box.thermostat_feature}/value`, { value });
    } catch (e) {
      console.error(e);
    }
  };

  angleToTemp = angleDeg => angleToSetpoint(angleDeg, this.getMinTemp(), this.getMaxTemp());

  onPointerDown = e => {
    if (!this.svgRef) return;
    const angle = getAngleFromPointer(e, this.svgRef);
    if (!isAngleInArc(angle)) return;
    // Only once the press is known to be on the ring: preventing the default
    // beforehand swallowed a touch starting anywhere on the gauge — the middle,
    // where the temperatures are — and a thumb scrolling past a dashboard of
    // thermostats found dead zones instead of the page moving.
    e.preventDefault();
    // Leaving 'off' by dragging the gauge only changes the preset locally here.
    // Writing PRESET now would debounce a regulation pass while MANUAL_MODE is
    // still false, so a drag lasting longer than the debounce — or an unmount
    // before the release — would let the loop apply the preset and start the
    // heater without the user ever having released a setpoint. It is written on
    // release instead, next to MANUAL_MODE and the setpoint.
    // A stop, not a programme point on Off: on that point the drag is a hold like
    // on any other, and the programme takes the thermostat back at its next point.
    const leavingOff = this.isStopped();
    const presetOnRelease = leavingOff ? this.getLastActivePreset() : null;
    // Snapshot of what the drag is about to overwrite locally, so a cancelled
    // gesture can put it back untouched.
    const stateBeforeDrag = {
      setpoint: this.state.setpoint,
      isManualMode: this.state.isManualMode,
      activePreset: this.state.activePreset,
      manualSetpointOverride: this.state.manualSetpointOverride
    };
    this.setState({
      setpoint: this.angleToTemp(angle),
      isDragging: true,
      isManualMode: true,
      manualSetpointOverride: true,
      ...(leavingOff ? { activePreset: presetOnRelease } : {})
    });
    // MANUAL_MODE is written on release, together with the setpoint and the
    // expiry: writing it here would leave the device in manual mode with no
    // MANUAL_UNTIL if the box unmounts mid-drag, and the regulation loop would
    // then hold the switch in its current state indefinitely.
    let lastDragSetpoint = this.angleToTemp(angle);
    this._onMove = ev => {
      ev.preventDefault();
      const a = getAngleFromPointer(ev, this.svgRef);
      if (isAngleInArc(a)) {
        lastDragSetpoint = this.angleToTemp(a);
        // Only local state during the drag — the server is notified once, on release
        this.setState({ setpoint: lastDragSetpoint });
      }
    };
    this._onUp = async () => {
      this.stopDrag();
      if (presetOnRelease) {
        this.startingFromOff = this.startOnPreset(presetOnRelease);
      }
      await this.sendSetpointOnceStarted(lastDragSetpoint);
      // A hold only expires when a schedule would otherwise take it over.
      if (this.state.activeSchedule) this.showManualCountdown();
    };
    // A drag taken over by the browser (scroll, gesture, window switch) fires
    // cancel and never up. That is an aborted gesture, not a release: committing
    // it would write a setpoint the user never chose — and on a thermostat left
    // on 'off', a finger caught by a wall-tablet scroll would restore the last
    // preset and start the heater. Roll the local state back and persist nothing,
    // exactly like an unmount mid-drag.
    this._onCancel = () => {
      this.stopDrag();
      this.setState(stateBeforeDrag);
    };
    window.addEventListener('pointermove', this._onMove);
    window.addEventListener('pointerup', this._onUp);
    window.addEventListener('touchmove', this._onMove, { passive: false });
    window.addEventListener('touchend', this._onUp);
    window.addEventListener('pointercancel', this._onCancel);
    window.addEventListener('touchcancel', this._onCancel);
  };

  stopDrag = () => {
    if (this._onMove) window.removeEventListener('pointermove', this._onMove);
    if (this._onUp) window.removeEventListener('pointerup', this._onUp);
    if (this._onMove) window.removeEventListener('touchmove', this._onMove);
    if (this._onUp) window.removeEventListener('touchend', this._onUp);
    if (this._onCancel) window.removeEventListener('pointercancel', this._onCancel);
    if (this._onCancel) window.removeEventListener('touchcancel', this._onCancel);
    this._onMove = null;
    this._onUp = null;
    this._onCancel = null;
    this.setState({ isDragging: false });
  };

  increment = () => {
    const step = 0.5;
    this.stepSetpoint(Math.min(this.getMaxTemp(), this.state.setpoint + step));
  };

  decrement = () => {
    const step = 0.5;
    this.stepSetpoint(Math.max(this.getMinTemp(), this.state.setpoint - step));
  };

  // A tap on + or −. On a stopped thermostat it also starts it again, on the
  // preset it was last on, and holds the new setpoint over that preset. On a
  // programme point on Off it is a hold like on any other point: the preset stays
  // `schedule`, and the programme takes the thermostat back at its next point —
  // writing a named preset there took it off its programme for good.
  //
  // Starting from a stop takes three writes, and their order matters: the mode,
  // since the loop skips a stopped thermostat; then the preset; then the
  // setpoint. The preset arms a hold on its own setpoint, so a setpoint sent
  // alongside it could land first and be replaced: the card showed 21.5 °C in
  // manual mode while the server held Comfort's 21 °C. A tap that follows before
  // the preset has landed waits for it too, or its setpoint would be replaced
  // the same way.
  stepSetpoint = async newSetpoint => {
    if (this.isStopped()) {
      const lastPreset = this.getLastActivePreset();
      this.setState({
        setpoint: newSetpoint,
        isManualMode: true,
        activePreset: lastPreset,
        manualSetpointOverride: true
      });
      this.startingFromOff = this.startOnPreset(lastPreset);
    } else {
      this.setState({ setpoint: newSetpoint, isManualMode: true, manualSetpointOverride: true });
    }
    if (this.state.activeSchedule) this.showManualCountdown();
    await this.sendSetpointOnceStarted(newSetpoint);
  };

  // A setpoint from + / − or the dial goes out once a start from a stop in
  // flight — this gesture's or an earlier one's — has landed its preset. The dial
  // used to send at once: after a tap on + from a stop, a drag released before
  // the preset landed was replaced by the preset's own hold.
  sendSetpointOnceStarted = async value => {
    const startingFromOff = this.startingFromOff;
    if (startingFromOff) {
      await startingFromOff;
      if (this.startingFromOff === startingFromOff) this.startingFromOff = null;
    }
    await this.sendSetpoint(value);
  };

  // Leaving a stop from the dial or the + and − buttons: start the thermostat
  // again, then put it back on the preset it was last on. Nothing reloads the card
  // afterwards; the pending mode writeMode holds is what keeps it from reading as
  // stopped, and from ignoring the hold the next writes arm.
  startOnPreset = async preset => {
    await this.resumeIfStopped();
    await this.savePreset(preset);
  };

  // Picking a preset is a single write on the preset feature. The server arms the
  // hold on that preset's setpoint and regulates on it: nothing else to send, and
  // no sequence of writes for the two sides to disagree about.
  //
  // `off` is the exception, because stopping is a mode rather than a preset: it
  // goes to the mode feature instead.
  selectPreset = async preset => {
    this.saveLastActivePreset(this.state.activePreset);
    const hasSchedule = !!this.state.activeSchedule;
    const newSetpoint =
      preset.temp !== null && preset.temp !== undefined ? this.toSetpointFeatureUnit(preset.temp) : this.state.setpoint;
    this.setState({
      activePreset: preset.key,
      setpoint: newSetpoint,
      isManualMode: hasSchedule,
      manualSetpointOverride: false
    });
    // The hold the server arms is the widget's own, so the event it echoes back
    // must not un-highlight the preset that was just picked.
    this.pickingPreset = true;
    try {
      if (preset.key === 'off') {
        // Stopping is a mode, not a preset: it switches the machine off, and the
        // regulation loop then leaves it alone whatever the programme says.
        // External thermostats carry this mode feature too — it is Gladys's own
        // stop, and the server turns it into the real device's stop (its mode
        // when it has one, plus the frost setpoint).
        await this.writeMode(THERMOSTAT_MODE.OFF);
      } else {
        await this.resumeIfStopped();
        await this.savePreset(preset.key);
      }
    } finally {
      this.pickingPreset = false;
    }
    await this.refreshFromDevice();
    if (hasSchedule && preset.key !== 'off') this.showManualCountdown();
  };

  render(
    props,
    {
      setpoint,
      currentTemp,
      humidity,
      activePreset,
      error,
      noConfig,
      isManualMode,
      manualUntil,
      manualSetpointOverride,
      isWindowOpen,
      isSwitchOn,
      pilotWireOrder,
      pilotWireSupportedOptions,
      activeSchedule
    }
  ) {
    const cfg = this.getConfig();
    const minTemp = this.getMinTemp();
    const maxTemp = this.getMaxTemp();
    const configMode = cfg.default_mode || 'heating';
    // The gauge builds one sentence out of these for screen readers; taken from
    // the dictionary directly because it composes them with live values rather
    // than rendering <Text> nodes.
    const a11yDict = (props.intl && props.intl.dictionary && props.intl.dictionary.dashboard.boxes.thermostat) || {};
    const a11yLabels = {
      setpoint: a11yDict.a11ySetpoint,
      currentTemp: a11yDict.a11yCurrentTemp,
      humidity: a11yDict.a11yHumidity,
      windowOpen: configMode === 'cooling' ? a11yDict.windowOpenCooling : a11yDict.windowOpen,
      // Shown in place of the setpoint when the thermostat is off: the number
      // the gauge would otherwise draw is the frost-protection fallback, not
      // something the user asked for (see the gauge).
      off: (a11yDict.preset && a11yDict.preset.off) || 'Off'
    };
    // Off is what the thermostat does when it is stopped, or on a programme point
    // on Off — unless a setpoint set by hand holds over that point: the loop
    // regulates on the hold until the next point, and the gauge shows it.
    const heldOverOff = activePreset === 'off' && isManualMode && manualSetpointOverride;
    const mode = activePreset === 'off' && !heldOverOff ? 'off' : configMode;
    const presets = this.getPresets();
    const hystStart = numOr(cfg.hysteresis_start, DEFAULT_HYSTERESIS_START);
    const hasCurrent = currentTemp !== null && currentTemp !== undefined;
    // TPI modulates the on-time over a cycle instead of switching on a band, so
    // painting the gauge with the hysteresis thresholds would show the widget
    // idle while the server is actually pulsing the heater. Under TPI the gauge
    // follows the demand: any positive error within the proportional band counts
    // as heating. Cooling always uses hysteresis, TPI included (see the server).
    const isTpi = cfg.control_type === 'tpi' && mode === 'heating';
    // Estimate, used only when no switch is configured to report the truth.
    // Hysteresis has a neutral zone — between setpoint − start and setpoint + stop
    // the server holds whatever the switch was already doing — so temperature
    // alone cannot say whether it is running. The estimate errs towards "off"
    // there, which is why the flame used to disappear while the relay was still
    // closed.
    const estimatedActive = (() => {
      if (!hasCurrent || mode === 'off') {
        return false;
      }
      // A never-driven thermostat shows a local comfort setpoint so the card is
      // not empty, but nothing was written and the server regulates nothing.
      // Estimating from that proposal would light the flame against a number the
      // user never chose; only a real switch state may say it is running.
      if (activePreset === null || activePreset === undefined) {
        return false;
      }
      if (isTpi) {
        return currentTemp < setpoint;
      }
      if (mode === 'heating') {
        return currentTemp < setpoint - hystStart;
      }
      if (mode === 'cooling') {
        return currentTemp > setpoint + hystStart;
      }
      return false;
    })();
    // The switch is the actuator the server drives, so its state is the truth:
    // the flame then matches what the heater is really doing, neutral zone included.
    // A pilot-wire heater regulating on its own setpoint: nothing tells whether
    // it heats, and estimating from Gladys's setpoint would light a flame on a
    // decision Gladys does not make. There is no setpoint to turn either: the
    // gauge shows the room temperature in its place, ringed in the colour of the
    // order the module confirms, a banner names that order, and the presets
    // below are the only control.
    const heaterRegulated = this.isHeaterRegulated();
    const orderLabels =
      (props.intl &&
        props.intl.dictionary &&
        props.intl.dictionary.deviceFeatureAction &&
        props.intl.dictionary.deviceFeatureAction.category.heater['pilot-wire-mode']) ||
      {};
    const orderLabel = order => orderLabels[PILOT_WIRE_ORDER_KEYS[order]] || '—';
    const confirmedPreset = heaterRegulated ? PRESET_OF_PILOT_WIRE_ORDER[pilotWireOrder] : undefined;
    // The ring takes that order's colour — through the dark theme's inversion
    // too, as the preset buttons' pills do.
    const confirmedColor = confirmedPreset ? this.getPresetColor(confirmedPreset) : null;
    const ringColor = confirmedColor && this.isDarkTheme() ? counterDarkModeInvert(confirmedColor) : confirmedColor;
    // The order the module should carry, from what the card shows: off when
    // stopped, window open or not, frost protection with the window open,
    // otherwise the order the lit preset is set to. A setpoint turned by a scene
    // names no preset, and is not second-guessed.
    let wantedOrder = null;
    if (heaterRegulated && mode === 'off') {
      wantedOrder = PILOT_WIRE_MODE.OFF;
    } else if (heaterRegulated && isWindowOpen) {
      wantedOrder = PILOT_WIRE_MODE.FROST_PROTECTION;
    } else if (heaterRegulated && isStartPreset(activePreset) && !manualSetpointOverride) {
      const configured = cfg[`pilot_wire_order_${activePreset}`];
      wantedOrder = Number.isInteger(configured) ? configured : DEFAULT_PILOT_WIRE_ORDERS[activePreset];
    }
    // The order actually sent, as the server picks it: one the module does not
    // take is sent as comfort, or not at all — and is then on its way for ever.
    const expectedOrder = wantedOrder === null ? null : supportedPilotWireOrder(wantedOrder, pilotWireSupportedOptions);
    // Until the module confirms it — a few seconds on Z-Wave — the banner says
    // the order is on its way rather than showing a ring and a preset that
    // disagree. Any comfort the heater keeps is comfort, as the server holds it.
    const comfortOrders = [PILOT_WIRE_MODE.COMFORT, PILOT_WIRE_MODE.COMFORT_1, PILOT_WIRE_MODE.COMFORT_2];
    const keptComfort = comfortOrders.includes(expectedOrder) && comfortOrders.includes(pilotWireOrder);
    const orderPending =
      expectedOrder !== null && pilotWireOrder !== null && pilotWireOrder !== expectedOrder && !keptComfort;
    // A preset held on the card has its own banner, "Comfort until 06:30", and
    // "Order: Comfort" above it said the same thing twice. The order banner
    // only stays when it tells more: an order on its way, or one other than the
    // preset's own (a comfort -1 pressed on the module).
    const holdNamesOrder =
      isManualMode &&
      !!manualUntil &&
      !manualSetpointOverride &&
      !!activeSchedule &&
      !orderPending &&
      pilotWireOrder === expectedOrder &&
      confirmedPreset === activePreset;
    const isRunning = isSwitchOn !== null ? isSwitchOn : estimatedActive;
    const showActive = !heaterRegulated && !isWindowOpen && mode !== 'off' && isRunning;
    // When Gladys regulates a pilot-wire heater, the module only ever carries
    // comfort or frost protection, whatever the preset: eco at 18 °C in a room at
    // 20 °C shows frost protection on the heater. Saying why, under the gauge,
    // is what keeps that from looking like a wrong order.
    const pilotWireStatus =
      this.isGladysRegulatedPilotWire() && !isWindowOpen && mode !== 'off' && isSwitchOn !== null
        ? isSwitchOn
          ? 'pilotWireHeating'
          : 'pilotWireStandby'
        : null;

    // Convert temperatures for display
    const displaySetpoint = this.toDisplayTemp(setpoint);
    const displayCurrentTemp = this.toDisplayTemp(currentTemp);
    const displayMinTemp = this.toDisplayTemp(minTemp);
    const displayMaxTemp = this.toDisplayTemp(maxTemp);
    const tempUnit = this.getTempUnit();

    return (
      <div class="card">
        {/* The thermostat's own name when the box was not given one: two
            thermostats side by side on a dashboard were otherwise two identical
            cards with no way to tell which room each drove. */}
        {(props.box.name || cfg.device_name) && (
          <div class="card-header">
            <h3 class="card-title">{props.box.name || cfg.device_name}</h3>
          </div>
        )}
        <div class="card-body">
          {error && (
            <div class="alert alert-danger">
              <i class="fe fe-alert-triangle mr-2" />
              <Text id="dashboard.boxes.thermostat.error" />
            </div>
          )}
          {noConfig && (
            <div class="alert alert-warning">
              <i class="fe fe-alert-triangle mr-2" />
              <Text id="dashboard.boxes.thermostat.noConfig" />
            </div>
          )}
          {!error && !noConfig && setpoint !== null && (
            <div>
              <div class="d-flex justify-content-center mb-3">
                <div ref={el => (this.svgRef = el)} class={style.gaugeContainer}>
                  <CircularGauge
                    key={`gauge-${mode}`}
                    setpoint={displaySetpoint}
                    animate={!this.state.isDragging && this.state.setpointLoaded}
                    currentTemp={displayCurrentTemp}
                    humidity={humidity}
                    onPointerDown={heaterRegulated ? undefined : this.onPointerDown}
                    onIncrement={heaterRegulated ? undefined : this.increment}
                    onDecrement={heaterRegulated ? undefined : this.decrement}
                    minTemp={displayMinTemp}
                    maxTemp={displayMaxTemp}
                    mode={mode}
                    isActive={showActive}
                    isWindowOpen={isWindowOpen}
                    tempUnit={tempUnit}
                    step={this.needsFahrenheitConversion() ? 0.9 : 0.5}
                    readOnly={heaterRegulated}
                    ringColor={ringColor}
                    a11yLabels={a11yLabels}
                  />
                </div>
              </div>

              {/* The order a pilot-wire heater regulating itself carries, drawn
                  like the card's other banners in that order's colour and icon;
                  greyed while one is on its way. */}
              {confirmedPreset && !holdNamesOrder && (
                <div
                  class={style.scheduleBanner}
                  style={`--banner-color:${orderPending ? this.getPresetColor('off') : confirmedColor}`}
                >
                  <i
                    class={`fe ${
                      orderPending
                        ? 'fe-loader'
                        : confirmedPreset === 'comfort'
                        ? COMFORT_ICON.heating
                        : PRESET_ICONS[confirmedPreset]
                    } ${style.scheduleBannerIcon}`}
                  />
                  <span class={style.scheduleBannerText}>
                    {orderPending ? (
                      <Text
                        id="dashboard.boxes.thermostat.pilotWireOrderPending"
                        fields={{ order: orderLabel(expectedOrder) }}
                      />
                    ) : (
                      <Text
                        id="dashboard.boxes.thermostat.pilotWireOrderCurrent"
                        fields={{ order: orderLabel(pilotWireOrder) }}
                      />
                    )}
                  </span>
                </div>
              )}

              {/* Drawn like the card's other banners. Heating carries comfort's
                  flame and colour, the order the module then carries; standby
                  carries frost's snowflake and colour, for the same reason. */}
              {pilotWireStatus && (
                <div
                  class={style.scheduleBanner}
                  style={`--banner-color:${this.getPresetColor(
                    pilotWireStatus === 'pilotWireHeating' ? 'comfort' : 'frost'
                  )}`}
                >
                  <i
                    class={`fe ${pilotWireStatus === 'pilotWireHeating' ? COMFORT_ICON.heating : PRESET_ICONS.frost} ${
                      style.scheduleBannerIcon
                    }`}
                  />
                  <span class={style.scheduleBannerText}>
                    <Text id={`dashboard.boxes.thermostat.${pilotWireStatus}`} />
                  </span>
                </div>
              )}

              {isWindowOpen && (
                <div class={style.windowBanner}>
                  {/* An alert glyph, not a window: the icon font has none, and
                      what matters here is that the heating is suspended. */}
                  <i class={`fe fe-alert-triangle ${style.windowBannerIcon}`} />
                  <span class={style.windowBannerText}>
                    {/* The server cuts the switch whatever the mode, so an open
                        window suspends a running air conditioner just as it
                        suspends a heater: the banner has to name the right one. */}
                    <Text
                      id={
                        configMode === 'cooling'
                          ? 'dashboard.boxes.thermostat.windowOpenCooling'
                          : 'dashboard.boxes.thermostat.windowOpen'
                      }
                    />
                  </span>
                </div>
              )}

              {/* A thermostat that has never been driven has no stored preset.
                  Hiding the bar then removed the only way to pick one — the bar
                  is shown with nothing highlighted instead. */}
              {(() => {
                const hasSchedule = !!activeSchedule;
                // The banner says what the thermostat is following; the bar
                // below stays reachable whatever it says. Replacing the bar
                // with the banner meant "I am away for the weekend -> Frost"
                // required detaching the programme on the integration page.
                // A setpoint set by hand on a preset's temperature shows as
                // that preset, on the bar and in the banner alike.
                // Not over an Off point, though: no preset is in force there, so a
                // unit switched on from its remote at Comfort's 21 °C read as
                // "Comfort until…" although it holds over the programme's Off
                // (forum 11074). It is a manual setpoint, and says so.
                const matchedPreset =
                  manualSetpointOverride && !this.isStopped() && activePreset !== 'off'
                    ? this.presetMatchingSetpoint(setpoint)
                    : null;
                const banner = (() => {
                  // The window banner above already says what the thermostat
                  // is doing, and it replaces this one.
                  // A stopped thermostat keeps its banner whatever else is
                  // true: the cross in it is the only way to start it again, and
                  // on one following no schedule there was none at all — the
                  // gauge read "Off", no preset was lit, and nothing on the card
                  // turned it back on.
                  if (isWindowOpen || (activePreset !== 'off' && (!hasSchedule || activePreset === null))) {
                    return null;
                  }
                  if (isManualMode && manualUntil) {
                    const untilDate = new Date(manualUntil);
                    const untilTime = `${String(untilDate.getHours()).padStart(2, '0')}:${String(
                      untilDate.getMinutes()
                    ).padStart(2, '0')}`;
                    const t = props.intl && props.intl.dictionary && props.intl.dictionary.dashboard.boxes.thermostat;
                    const untilWhen = untilText(
                      calendarDaysAhead(manualUntil),
                      (untilDate.getDay() + 6) % 7,
                      untilTime,
                      t
                    );
                    const manualUntilLabel = (t && t.manualUntil) || '';
                    const cancelLabel = (t && t.cancelManual) || '';
                    // A hold is not always a hand-set temperature: picking a
                    // preset arms one too. Saying "Manual mode" while the
                    // Night button is lit told the user their choice had been
                    // replaced by something else. `manualSetpointOverride` is
                    // exactly the distinction — it is what un-highlights the
                    // preset — so the banner names the preset whenever one is
                    // still highlighted, and keeps "Manual mode" for a
                    // setpoint that no preset carries.
                    let heldPreset =
                      !manualSetpointOverride && [...HEATING_PRESETS, ...COOLING_PRESETS].includes(activePreset)
                        ? presets.find(candidate => candidate.key === activePreset)
                        : null;
                    if (matchedPreset) {
                      heldPreset = presets.find(candidate => candidate.key === matchedPreset);
                    }
                    const i18nHeldPresets =
                      props.intl && props.intl.dictionary && props.intl.dictionary.dashboard.boxes.thermostat.preset;
                    const label = heldPreset
                      ? (i18nHeldPresets && i18nHeldPresets[heldPreset.key]) || heldPreset.key
                      : (t && t.manualMode) || '';
                    return (
                      <div
                        class={cx(style.manualBanner, { [style.manualBannerPreset]: !!heldPreset })}
                        style={heldPreset ? `--banner-color:${this.getPresetColor(heldPreset.key)}` : undefined}
                      >
                        <i class={`fe ${heldPreset ? heldPreset.icon : 'fe-user'} ${style.manualBannerIcon}`} />
                        <span class={style.manualBannerText}>
                          {label}
                          <span class={style.manualBannerUntil}>
                            {' '}
                            {manualUntilLabel} {untilWhen}
                          </span>
                        </span>
                        <button class={style.manualBannerCancel} onClick={this.cancelManualMode} title={cancelLabel}>
                          <i class="fe fe-x" />
                        </button>
                      </div>
                    );
                  }

                  // Planning banner: preset icon + name + next transition time
                  const knownPresetKeys = [...HEATING_PRESETS, ...COOLING_PRESETS];
                  const resolvedPresetKey = knownPresetKeys.includes(activePreset) ? activePreset : 'comfort';
                  const activePresetObj =
                    presets.find(p => p.key === resolvedPresetKey) ||
                    presets.find(p => p.key === 'comfort') ||
                    presets[0];
                  const presetIcon = activePresetObj ? activePresetObj.icon : 'fe-power';
                  const i18nPresets =
                    props.intl && props.intl.dictionary && props.intl.dictionary.dashboard.boxes.thermostat.preset;
                  const presetName =
                    i18nPresets && i18nPresets[resolvedPresetKey] ? i18nPresets[resolvedPresetKey] : resolvedPresetKey;
                  const bannerColor = this.getPresetColor(resolvedPresetKey);
                  const t2 = props.intl && props.intl.dictionary && props.intl.dictionary.dashboard.boxes.thermostat;
                  const untilLabel = (t2 && t2.scheduleUntil) || '';
                  // Counted by the server from the house's today: the phone's
                  // may already be tomorrow, or still yesterday.
                  const nextPoint = activeSchedule && activeSchedule.next;
                  const nextWhen = nextPoint
                    ? untilText(nextPoint.days_ahead, nextPoint.day_of_week, nextPoint.time, t2)
                    : null;

                  // A stopped thermostat is not waiting for the next point:
                  // the programme does not start it again, only a mode does.
                  // Announcing an hour here promises a return that will not
                  // happen — and the cross is the only way back to the
                  // programme, since every preset arms a hold instead.
                  //
                  // Stopped is the mode, not the preset: a programme point on
                  // `off` lights Off too, yet the thermostat is following the
                  // programme, and that point ends at the next one like any
                  // other ("Off until 06:30"). Reading it from the preset
                  // offered "Back to the schedule" on a thermostat already
                  // back on it, and the button then did nothing.
                  const stopped = this.isStopped();
                  const exitStoppedLabel = hasSchedule
                    ? (t2 && t2.backToSchedule) || ''
                    : (t2 && t2.resumeFromStopped) || '';

                  return (
                    <div class={style.scheduleBanner} style={`--banner-color:${bannerColor}`}>
                      <i class={`fe ${presetIcon} ${style.scheduleBannerIcon}`} />
                      {/* A stopped thermostat already says so in the middle of
                              the gauge, in the largest text on the card, and again
                              under the Off button. Repeating the word a third time
                              here said nothing the user had not read twice — but
                              the banner still has to appear, because its cross is
                              the only way back to the programme. */}
                      <span class={style.scheduleBannerText}>
                        {stopped ? exitStoppedLabel : presetName}
                        {!stopped && activeSchedule && activeSchedule.next && (
                          <span class={style.scheduleBannerUntil}>
                            {' '}
                            {untilLabel} {nextWhen}
                          </span>
                        )}
                      </span>
                      {stopped && (
                        <button
                          class={style.manualBannerCancel}
                          onClick={hasSchedule ? this.cancelManualMode : this.resumeFromStopped}
                          title={exitStoppedLabel}
                        >
                          {/* A cross reads as "dismiss": this one starts the
                              thermostat, so it carries the play glyph. */}
                          <i class="fe fe-play" />
                        </button>
                      )}
                    </div>
                  );
                })();

                // A null preset means the user has not chosen one yet, so
                // nothing is highlighted — falling back to 'comfort' would
                // claim a setting that was never made, and the widget writes
                // none until it is clicked.
                const resolvedActivePreset = [...HEATING_PRESETS, ...COOLING_PRESETS].includes(activePreset)
                  ? activePreset
                  : null;
                // Stopping is a mode, and a mode leaves the preset feature alone:
                // a preset set by a scene on a stopped thermostat is stored, and
                // is what it will run on when it starts again. Nothing said so —
                // no button was lit — so the scene looked as though it had done
                // nothing. Shown dimmed: the shape says which preset, the dimming
                // says the machine is not running it yet.
                // The dimming says it visually; this says it in words, for a
                // screen reader and for a hover.
                const pendingTitle = label => {
                  const template =
                    props.intl &&
                    props.intl.dictionary &&
                    props.intl.dictionary.dashboard.boxes.thermostat.presetPending;
                  return template ? template.replace('{{preset}}', label) : label;
                };
                const storedPreset = this.state.remoteConfig && PRESET_NAMES[this.state.remoteConfig.preset];
                const pendingPreset =
                  activePreset === 'off' &&
                  storedPreset !== 'off' &&
                  [...HEATING_PRESETS, ...COOLING_PRESETS].includes(storedPreset)
                    ? storedPreset
                    : null;
                return (
                  <div>
                    {banner}
                    {/* Dimmed rather than removed while a window is open: the
                            bar used to disappear, and the card lost 56px of height
                            — every widget beside it on the dashboard shifted, and
                            came back when the window was shut. */}
                    <div
                      class={cx(style.segmentedControl, { [style.segmentedControlDisabled]: isWindowOpen })}
                      aria-disabled={isWindowOpen ? 'true' : undefined}
                    >
                      {presets.map(preset => {
                        const presetTitle =
                          props.intl &&
                          props.intl.dictionary &&
                          props.intl.dictionary.dashboard &&
                          props.intl.dictionary.dashboard.boxes &&
                          props.intl.dictionary.dashboard.boxes.thermostat &&
                          props.intl.dictionary.dashboard.boxes.thermostat.preset &&
                          props.intl.dictionary.dashboard.boxes.thermostat.preset[preset.key]
                            ? props.intl.dictionary.dashboard.boxes.thermostat.preset[preset.key]
                            : preset.key;
                        const isActive =
                          (resolvedActivePreset === preset.key && !manualSetpointOverride) ||
                          matchedPreset === preset.key;
                        const isPending = pendingPreset === preset.key;
                        const presetColor = this.getPresetColor(preset.key);
                        return (
                          <button
                            key={preset.key}
                            class={cx(style.segmentBtn, {
                              [style.segmentBtnActive]: isActive,
                              [style.segmentBtnPending]: isPending,
                              [style.segmentBtnOff]: preset.key === 'off'
                            })}
                            style={
                              isActive || isPending
                                ? `--preset-color:${presetColor};--preset-text:${readableTextOn(presetColor)}`
                                : undefined
                            }
                            onClick={() => this.selectPreset(preset)}
                            title={isPending ? pendingTitle(presetTitle) : presetTitle}
                            aria-pressed={isActive ? 'true' : 'false'}
                            disabled={isWindowOpen}
                          >
                            <i class={`fe ${preset.icon}`} />
                            {/* The name under the icon: `title` only shows on
                                    hover, so on a wall tablet the bar was six
                                    unlabelled pictograms. */}
                            <span class={style.segmentBtnLabel}>{presetTitle}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default connect('httpClient,session,user,darkMode', {})(withIntlAsProp(ThermostatBox));
